import { useState, useEffect, useCallback, useRef } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { DataTable } from '../components/shared/DataTable';
import { AIAgentModal } from '../components/ai-agents/AIAgentModal';
import { ViewAIAgentModal } from '../components/ai-agents/ViewAIAgentModal';
import { searchAIAgents, deleteAIAgent } from '../services/aiAgentService';
import { useProfileStore } from '../store/useProfileStore';
import { isCompanyShopAdmin } from '../types/profile';
import { useBreadcrumbStore } from '../store/useBreadcrumbStore';
import { useRefresh } from '../contexts/RefreshContext';
import type { RetellAIAgent } from '../types/aiAgent';
import type { Column } from '../components/shared/DataTable';

const columns: Column<RetellAIAgent>[] = [
  {
    key: 'active',
    label: 'Status',
    render: (item) => (
      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${item.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
        {item.active ? 'Active' : 'Inactive'}
      </span>
    ),
  },
  { key: 'retellAgentId', label: 'Retell Agent Id' },
  { key: 'name', label: 'Name' },
  {
    key: 'contextData',
    label: 'Context Data',
    render: (item) =>
      !item.contextData?.length ? '—' : (
        <div className="flex flex-col gap-0.5">
          {item.contextData.map((d, i) => (
            <div key={i} className="text-sm">{d.key}</div>
          ))}
        </div>
      ),
  },
];

function RowMenu({ item, canEdit, onView, onEdit, onDelete }: { item: RetellAIAgent; canEdit: boolean; onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
          {canEdit ? (
            <>
              <button
                onClick={() => { setOpen(false); onEdit(); }}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                Edit
              </button>
              <button
                onClick={() => { setOpen(false); onDelete(); }}
                className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </>
          ) : (
            <button
              onClick={() => { setOpen(false); onView(); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              View
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AIAgentsPage() {
  const location = useLocation();
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const canEdit = isCompanyShopAdmin(activeProfile);
  const [data, setData] = useState<RetellAIAgent[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [modalAgent, setModalAgent] = useState<RetellAIAgent | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);
  const [viewAgent, setViewAgent] = useState<RetellAIAgent | null>(null);
  const setBreadcrumb = useBreadcrumbStore((s) => s.set);

  useEffect(() => {
    if (location.pathname !== '/ai-agents') return;
    setBreadcrumb([{ label: 'AI Agents' }]);
  }, [location.pathname, setBreadcrumb]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await searchAIAgents({}, { page, pageSize, orderBy: 'updated', order: 'desc' });
      setData(results);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => { fetch(); }, [fetch]);

  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();
  useEffect(() => {
    registerRefreshHandler('/ai-agents', fetch);
    return () => unregisterRefreshHandler('/ai-agents');
  }, [fetch, registerRefreshHandler, unregisterRefreshHandler]);

  function openCreate() {
    setModalAgent(undefined);
    setShowModal(true);
  }

  function openEdit(item: RetellAIAgent) {
    setModalAgent(item);
    setShowModal(true);
  }

  async function handleDelete(id: string) {
    try {
      await deleteAIAgent(id);
      fetch();
    } catch {
      // error handled by apiService
    }
  }

  return (
    <IonPage>
      <IonContent>
        <div className="p-4 sm:p-6 lg:p-8">
          <DataTable
            title="AI Agents"
            columns={columns}
            data={data}
            page={page}
            pageSize={pageSize}
            loading={loading}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            onRefresh={fetch}
            onAdd={canEdit ? openCreate : undefined}
            alignTop
            rowMenu={(item) => (
              <RowMenu
                item={item}
                canEdit={canEdit}
                onView={() => setViewAgent(item)}
                onEdit={() => openEdit(item)}
                onDelete={() => handleDelete(item.id)}
              />
            )}
          />
          {showModal && (
            <AIAgentModal
              agent={modalAgent}
              onClose={() => setShowModal(false)}
              onSuccess={() => { setShowModal(false); fetch(); }}
            />
          )}
          {viewAgent && (
            <ViewAIAgentModal
              agent={viewAgent}
              onClose={() => setViewAgent(null)}
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
